import {useEffect, useState} from 'react'
import {Autocomplete, Box, CircularProgress, TextField, Typography} from '@mui/material'
import axios from 'axios'

interface StateSelectorProps {
    accountName: string
    selectedStates: string[]
    onStatesChange: (states: string[]) => void
}

function StateSelector({accountName, selectedStates, onStatesChange}: StateSelectorProps) {
    const [states, setStates] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string>('')

    useEffect(() => {
        const fetchStates = async () => {
            if (!accountName) {
                setStates([])
                return
            }

            setLoading(true)
            setError('')
            try {
                const {data} = await axios.get(`/api/admin/account/meta`)
                const stateValues = Object.values(data.stateMap || {}) as string[]
                setStates(stateValues.sort())
            } catch (err) {
                console.error('Failed to fetch account states:', err)
                setError('Failed to load states')
                setStates([])
            } finally {
                setLoading(false)
            }
        }

        fetchStates()
    }, [accountName])

    if (loading) {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <CircularProgress size={20}/>
                <Typography variant="body2" color="text.secondary">Loading states...</Typography>
            </Box>
        )
    }

    if (error) {
        return <Typography variant="body2" color="error">{error}</Typography>
    }

    if (!accountName) {
        return <Typography variant="body2" color="text.secondary">Enter an account name to load available states</Typography>
    }

    if (states.length === 0) {
        return <Typography variant="body2" color="text.secondary">No states available for this account</Typography>
    }

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1
            }}>
                <Typography variant="body2" sx={{fontWeight: 500}}>
                    States
                </Typography>
                <Box sx={{display: 'flex', gap: 1}}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': {textDecoration: 'underline'}
                        }}
                        onClick={() => onStatesChange(states)}
                    >
                        All
                    </Typography>
                    <Typography variant="caption" sx={{color: '#999'}}>|</Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': {textDecoration: 'underline'}
                        }}
                        onClick={() => onStatesChange([])}
                    >
                        None
                    </Typography>
                </Box>
            </Box>
            <Autocomplete
                multiple
                size="small"
                options={[...states]}
                value={selectedStates}
                onChange={(_, newValue) => {
                    onStatesChange(newValue)
                }}
                renderInput={(params) => (
                    <TextField {...params} placeholder={selectedStates.length === 0 ? 'Select states...' : ''}/>
                )}
                disableCloseOnSelect
                sx={{
                    '& .MuiAutocomplete-endAdornment': {
                        right: '7px !important'
                    },
                    '& .MuiInputBase-root': {
                        paddingRight: '11px !important',
                        backgroundColor: '#fff'
                    }
                }}
                slotProps={{
                    chip: {
                        size: 'small'
                    }
                }}
            />
        </Box>
    )
}

export default StateSelector
